import {
  ALPHABET,
  foldDiacritics,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * The pigpen (Freemasons') cipher in its standard modern form: two noughts-and-crosses
 * grids and two X shapes.
 *
 *     A | B | C      J·| K·| L·          S            W·
 *     --+---+--      --+---+--         T   U        X·  Y·
 *     D | E | F      M·| N·| O·          V            Z·
 *     --+---+--      --+---+--
 *     G | H | I      P·| Q·| R·
 *
 * A letter is written as the lines of the cell around it — A is the corner ⌟, E the
 * whole box — with a dot added for the second grid and the second X. A letter of an X
 * is the V-shaped wedge that holds it: S, at the top, is a V; T, on the left, is >.
 * This is the layout of the usual key (the public-domain chart on Wikipedia's "Pigpen
 * cipher" page); older and Masonic variants arrange the letters differently.
 */

export type WedgeOpening = "up" | "down" | "left" | "right";

export type PigpenShape =
  | {
      kind: "box";
      top: boolean;
      right: boolean;
      bottom: boolean;
      left: boolean;
    }
  | { kind: "wedge"; opens: WedgeOpening };

export interface PigpenGlyph {
  letter: string;
  shape: PigpenShape;
  dot: boolean;
}

const WEDGES: readonly WedgeOpening[] = ["up", "left", "right", "down"];

function boxGlyph(letter: string, cell: number, dot: boolean): PigpenGlyph {
  const row = Math.floor(cell / 3);
  const column = cell % 3;
  return {
    letter,
    dot,
    shape: {
      kind: "box",
      top: row > 0,
      right: column < 2,
      bottom: row < 2,
      left: column > 0,
    },
  };
}

function buildGlyphs(): PigpenGlyph[] {
  return [...ALPHABET].map((letter, index) => {
    if (index < 9) {
      return boxGlyph(letter, index, false);
    }
    if (index < 18) {
      return boxGlyph(letter, index - 9, true);
    }
    const wedge = (index - 18) % 4;
    return {
      letter,
      dot: index >= 22,
      shape: { kind: "wedge", opens: WEDGES[wedge] },
    };
  });
}

/** A–Z in order. */
export const PIGPEN_GLYPHS: readonly PigpenGlyph[] = buildGlyphs();

export function pigpenGlyph(letter: string): PigpenGlyph | null {
  const index = letterIndex(letter);
  return index < 0 ? null : PIGPEN_GLYPHS[index];
}

const round = (value: number) => Math.round(value * 100) / 100;

/** SVG path data for a glyph drawn in the square at (x, y) with side `size`. */
export function glyphPath(
  glyph: PigpenGlyph,
  x: number,
  y: number,
  size: number
): string {
  const left = round(x);
  const top = round(y);
  const right = round(x + size);
  const bottom = round(y + size);
  const middleX = round(x + size / 2);
  const middleY = round(y + size / 2);
  const { shape } = glyph;
  if (shape.kind === "wedge") {
    switch (shape.opens) {
      case "up":
        return `M${left} ${top}L${middleX} ${bottom}L${right} ${top}`;
      case "down":
        return `M${left} ${bottom}L${middleX} ${top}L${right} ${bottom}`;
      case "left":
        return `M${left} ${top}L${right} ${middleY}L${left} ${bottom}`;
      default:
        return `M${right} ${top}L${left} ${middleY}L${right} ${bottom}`;
    }
  }
  const segments: string[] = [];
  if (shape.top) {
    segments.push(`M${left} ${top}H${right}`);
  }
  if (shape.right) {
    segments.push(`M${right} ${top}V${bottom}`);
  }
  if (shape.bottom) {
    segments.push(`M${left} ${bottom}H${right}`);
  }
  if (shape.left) {
    segments.push(`M${left} ${top}V${bottom}`);
  }
  return segments.join("");
}

/** Centre of the dot, inside the cell or the mouth of the wedge; null when there is none. */
export function glyphDot(
  glyph: PigpenGlyph,
  x: number,
  y: number,
  size: number
): { cx: number; cy: number } | null {
  if (!glyph.dot) {
    return null;
  }
  const { shape } = glyph;
  let fx = 0.5;
  let fy = 0.5;
  if (shape.kind === "wedge") {
    const offsets: Record<WedgeOpening, [number, number]> = {
      up: [0.5, 0.3],
      down: [0.5, 0.7],
      left: [0.3, 0.5],
      right: [0.7, 0.5],
    };
    [fx, fy] = offsets[shape.opens];
  }
  return { cx: round(x + size * fx), cy: round(y + size * fy) };
}

/** One position in the written message: a letter, or a gap between words. */
export type PigpenCell = string | null;

export interface PigpenLayout {
  /** Lines of cells; `null` is a space. */
  lines: PigpenCell[][];
  /** Letters written. */
  letters: number;
  /** Characters with no pigpen symbol (digits, punctuation, other scripts), once each. */
  skipped: string[];
}

const WHITESPACE = /\s/u;
const LINE_BREAK = /\r?\n/;

function wordsOf(line: string, skipped: Set<string>): string[] {
  const words: string[] = [];
  let current = "";
  for (const char of foldDiacritics(line)) {
    const index = letterIndex(char);
    if (index >= 0) {
      current += ALPHABET.charAt(index);
    } else if (WHITESPACE.test(char)) {
      if (current !== "") {
        words.push(current);
      }
      current = "";
    } else {
      skipped.add(char);
    }
  }
  if (current !== "") {
    words.push(current);
  }
  return words;
}

function wrapWords(words: readonly string[], perLine: number): PigpenCell[][] {
  const lines: PigpenCell[][] = [];
  let line: PigpenCell[] = [];
  for (const word of words) {
    for (let start = 0; start < word.length; start += perLine) {
      const piece = [...word.slice(start, start + perLine)];
      const needed =
        line.length === 0 ? piece.length : line.length + 1 + piece.length;
      if (needed > perLine && line.length > 0) {
        lines.push(line);
        line = [];
      }
      if (line.length > 0) {
        line.push(null);
      }
      line.push(...piece);
    }
  }
  if (line.length > 0) {
    lines.push(line);
  }
  return lines;
}

/**
 * Lay a message out for drawing: words kept whole and wrapped at `perLine` symbols, the
 * reader's own line breaks kept, a word longer than a line split across lines.
 */
export function pigpenLayout(text: string, perLine: number): PigpenLayout {
  const skipped = new Set<string>();
  const lines: PigpenCell[][] = [];
  for (const line of text.split(LINE_BREAK)) {
    lines.push(...wrapWords(wordsOf(line, skipped), perLine));
  }
  const letters = lines.reduce(
    (sum, line) => sum + line.filter((cell) => cell !== null).length,
    0
  );
  return { lines, letters, skipped: [...skipped] };
}
